import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { cn } from "@/utils/cn";
import { useState } from "react";
import { WorkflowMarkdown } from "./WorkflowMarkdown";

/** A text to read: a step, the introduction, or another text of a workflow. */
export type TextTarget = {
  title: string;
  description: string;
  source?: string;
  markdown: string;
  planned: boolean;
  archived?: boolean;
};

type View = "rendered" | "raw";

const VIEWS: { id: View; label: string }[] = [
  { id: "rendered", label: "Instructions" },
  { id: "raw", label: "Raw text" },
];

const EMPTY = "This text is empty.";

/** One text of a workflow, as the agent gets it. */
export const WorkflowTextDialog = ({
  target,
  onClose,
}: {
  target: TextTarget;
  onClose: () => void;
}) => {
  const [view, setView] = useState<View>("rendered");
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-zinc-200 px-6 py-5 pr-12 sm:px-8">
          <DialogTitle>{target.title}</DialogTitle>
          <DialogDescription className="leading-relaxed">{target.description}</DialogDescription>
          {target.source && (
            <p className="break-words font-mono text-2xs text-zinc-500">{target.source}</p>
          )}
        </DialogHeader>
        <div
          role="tablist"
          aria-label="Text view"
          className="flex shrink-0 gap-1 border-b border-zinc-200 px-4 sm:px-6"
        >
          {VIEWS.map((item) => (
            <button
              key={item.id}
              id={`workflow-view-${item.id}`}
              type="button"
              role="tab"
              aria-selected={view === item.id}
              aria-controls="workflow-view-panel"
              onClick={() => setView(item.id)}
              className={cn(
                "-mb-px border-b-2 px-3 py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400",
                view === item.id
                  ? "border-zinc-900 text-zinc-900"
                  : "border-transparent text-zinc-500 hover:text-zinc-800",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div
          id="workflow-view-panel"
          role="tabpanel"
          aria-labelledby={`workflow-view-${view}`}
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {view === "raw" ? (
            <pre className="m-6 whitespace-pre-wrap break-words rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-xs leading-5 text-zinc-800 sm:m-8">
              {target.markdown || EMPTY}
            </pre>
          ) : (
            <div className="px-6 py-6 sm:px-8">
              {target.planned && (
                <p className="mb-5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  Planned. The agent does not get this text yet.
                </p>
              )}
              {target.archived && (
                <p className="mb-5 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700">
                  Archived. This is a copy of an earlier version. The agent no longer gets it.
                </p>
              )}
              {target.markdown ? (
                <WorkflowMarkdown>{target.markdown}</WorkflowMarkdown>
              ) : (
                <p className="text-sm text-zinc-600">{EMPTY}</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
